import { MessageTemplates } from "../../common/MessageTemplates";
import { useState, useEffect, useCallback } from "react";
import { MarkdownOrTextComponent } from "../../common/RenderMarkdownOrText";

const Avatar = require("../../../../icons/ai-bot.png");

function StepLoader({ message, languageText, setStep, step }) {
  useEffect(() => {
    if (!message?.isSpecialLoading) return;

    if (!message.specialLoadingStartMs) {
      message.specialLoadingStartMs = Date.now();
    }

    const interval = setInterval(() => {
      setStep((prev) => prev + 1);
    }, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [message]);

  const stepTexts = [
    languageText?.firstStep ?? "Step 1…",
    languageText?.secondStep ?? "Step 2…",
    languageText?.thirdStep ?? "Step 3…",
    languageText?.fourthStep ?? "Step 4…",
  ];

  const stepIndex = Math.max(0, Math.min(step, stepTexts.length - 1));

  return (
    <div className="shimmer-text" aria-live="polite">
      {stepTexts[stepIndex]}
    </div>
  );
}

const extractJsonFromContent = (content) => {
  if (!content || typeof content !== "string") return null;
  
  const trimmed = content.trim();
  
  // Case 1: Direct JSON object
  if (trimmed.startsWith("{")) {
    try {
      return JSON.parse(trimmed);
    } catch (e) {
      // Try to find JSON in markdown code block
    }
  }
  
  // Case 2: JSON in markdown code block ```json ... ```
  const jsonBlockRegex = /```json\s*([\s\S]*?)```/;
  const match = trimmed.match(jsonBlockRegex);
  if (match && match[1]) {
    try {
      return JSON.parse(match[1].trim());
    } catch (e) {
      // Invalid JSON in code block
    }
  }
  
  // Case 3: JSON in generic code block ``` ... ```
  const genericBlockRegex = /```\s*([\s\S]*?)```/;
  const genericMatch = trimmed.match(genericBlockRegex);
  if (genericMatch && genericMatch[1]) {
    const codeContent = genericMatch[1].trim();
    // Check if it looks like JSON
    if (codeContent.startsWith("{") || codeContent.startsWith("[")) {
      try {
        return JSON.parse(codeContent);
      } catch (e) {
        // Invalid JSON
      }
    }
  }
  
  // Case 4: Try to find JSON object anywhere in the content
  const jsonObjectRegex = /\{[\s\S]*"title"[\s\S]*"weekly_plan"[\s\S]*\}/;
  const jsonMatch = trimmed.match(jsonObjectRegex);
  if (jsonMatch && jsonMatch[0]) {
    try {
      return JSON.parse(jsonMatch[0]);
    } catch (e) {
      // Invalid JSON
    }
  }
  
  return null;
};

const parsePartialJson = (content) => {
  if (!content || typeof content !== "string") return null;
  
  const trimmed = content.trim();
  
  let jsonString = "";
  
  const jsonBlockRegex = /```json\s*([\s\S]*)/;
  const genericBlockRegex = /```\s*([\s\S]*)/;
  
  let match = trimmed.match(jsonBlockRegex);
  if (match && match[1]) {
    jsonString = match[1];
    // Remove closing ``` if exists
    jsonString = jsonString.replace(/```\s*$/, "");
  } else {
    match = trimmed.match(genericBlockRegex);
    if (match && match[1]) {
      jsonString = match[1];
      jsonString = jsonString.replace(/```\s*$/, "");
    } else if (trimmed.startsWith("{")) {
      // Direct JSON
      jsonString = trimmed;
    } else {
      // Try to find JSON object in content
      const jsonStart = trimmed.indexOf("{");
      if (jsonStart !== -1) {
        jsonString = trimmed.substring(jsonStart);
      }
    }
  }
  
  if (!jsonString || !jsonString.trim()) return null;
  
  // Try to parse complete JSON first
  try {
    const parsed = JSON.parse(jsonString.trim());
    if (parsed && (parsed.title || parsed.weekly_plan || parsed.learning_strategies)) {
      return parsed;
    }
  } catch (e) {
    // JSON is incomplete, try to "fix" it by closing brackets/braces
  }
  
  // Try to fix incomplete JSON by adding missing closing brackets
  let fixedJson = jsonString.trim();
  
  // Count opening and closing braces/brackets
  let openBraces = (fixedJson.match(/{/g) || []).length;
  let closeBraces = (fixedJson.match(/}/g) || []).length;
  let openBrackets = (fixedJson.match(/\[/g) || []).length;
  let closeBrackets = (fixedJson.match(/\]/g) || []).length;
  
  // Add missing closing brackets
  while (closeBrackets < openBrackets) {
    fixedJson += "]";
    closeBrackets++;
  }
  
  // Add missing closing braces
  while (closeBraces < openBraces) {
    fixedJson += "}";
    closeBraces++;
  }
  
  // Try to parse the fixed JSON
  try {
    const parsed = JSON.parse(fixedJson);
    if (parsed && (parsed.title || parsed.weekly_plan || parsed.learning_strategies)) {
      return parsed;
    }
  } catch (e) {
    // Still invalid, try to extract what we can
  }
  
  // Last resort: extract basic fields using regex
  const partialData = {};
  
  // Extract title (handle escaped quotes)
  const titleMatch = jsonString.match(/"title"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (titleMatch && titleMatch[1]) {
    partialData.title = titleMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
  }
  
  // Extract overview (handle escaped quotes and newlines)
  const overviewMatch = jsonString.match(/"overview"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (overviewMatch && overviewMatch[1]) {
    partialData.overview = overviewMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
  }
  
  // Try to extract weekly_plan by finding complete day objects
  const weeklyPlan = [];
  const dayPattern = /\{\s*"name"\s*:\s*"([^"]*)"\s*,\s*"time"\s*:\s*"([^"]*)"\s*,\s*"focus"\s*:\s*"([^"]*)"\s*,\s*"activity"\s*:\s*\[([\s\S]*?)\]\s*\}/g;
  let dayMatch;
  
  while ((dayMatch = dayPattern.exec(jsonString)) !== null) {
    try {
      const dayObj = {
        name: dayMatch[1],
        time: dayMatch[2],
        focus: dayMatch[3],
        activity: []
      };
      
      // Extract activities from the activity array
      const activityContent = dayMatch[4];
      const activityPattern = /\{\s*"item"\s*:\s*"((?:[^"\\]|\\.)*)"\s*\}/g;
      let activityMatch;
      
      while ((activityMatch = activityPattern.exec(activityContent)) !== null) {
        dayObj.activity.push({
          item: activityMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n')
        });
      }
      
      weeklyPlan.push(dayObj);
    } catch (e) {
      // Skip invalid day object
    }
  }
  
  if (weeklyPlan.length > 0) {
    partialData.weekly_plan = weeklyPlan;
  }
  
  // Only return if we have at least title or weekly_plan
  if (partialData.title || partialData.weekly_plan) {
    return partialData;
  }
  
  return null;
};

const RenderAiPlanner = ({ message, languageText, saveItems }) => {
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [parsedContent, setParsedContent] = useState(null);

  const isContentObject =
    typeof message.content === "object" && message.content !== null;

  useEffect(() => {
    if (message.content) {
      setIsLoading(false);
      
      if (typeof message.content === "string" && message.content.trim()) {
        let extracted = extractJsonFromContent(message.content);
        
        if (!extracted && message.isStreaming) {
          extracted = parsePartialJson(message.content);
        }
        
        if (extracted && (extracted.title || extracted.weekly_plan || extracted.learning_strategies)) {
          setParsedContent(extracted);
        } else {
          setParsedContent(null);
        }
      } else if (isContentObject) {
        if (message.content.title || message.content.weekly_plan || message.content.learning_strategies) {
          setParsedContent(message.content);
        } else {
          setParsedContent(null);
        }
      } else {
        setParsedContent(null);
      }
    } else if (step >= 2) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [step, message.content, message.isStreaming, isContentObject]);

  const renderContent = useCallback(() => {
    const hasLearningPlan = parsedContent !== null;
    
    const contentStr = typeof message.content === "string" ? message.content : "";
    const hasLearningPlanIndicators = 
      contentStr.includes('"title"') || 
      contentStr.includes('"weekly_plan"') || 
      contentStr.includes('"learning_strategies"') ||
      contentStr.includes("```json") ||
      (contentStr.includes("```") && contentStr.includes('"title"'));
    
    const shouldRenderTemplate = 
      isContentObject || 
      hasLearningPlan ||
      (typeof message.content === "string" && hasLearningPlanIndicators);
    
    const contentToRender = parsedContent || message.content;

    if (!shouldRenderTemplate && message.content) {
      return MarkdownOrTextComponent(
        message,
        message.content,
        message.isStreaming
      );
    } else {
      return (
        <MessageTemplates.plan
          content={contentToRender}
          languageText={languageText}
          saveItems={saveItems}
          features_id={message.features_id}
          is_saved={message.is_saved}
          isLoading={isLoading && !parsedContent && message.isStreaming}
          isStreaming={message.isStreaming}
        />
      );
    }
  }, [message, isContentObject, isLoading, parsedContent, languageText]);

  return (
    <div key={message.id} className={`message ai`}>
      <div className="message-container flex flex-column gap-2">
        {message.isSpecialLoading && (
          <StepLoader
            message={message}
            languageText={languageText}
            setStep={setStep}
            step={step}
          />
        )}

        <div className="flex align-items-start gap-2 message-wrapper">
          <div className="message-avatar">
            <img src={Avatar} alt="AI Avatar" />
          </div>
          <div className="message-content">
            <div
              className={`message-bubble ai ${message.isError ? "error" : ""}`}
            >
              {message.isStreaming && !message.content && !isLoading ? (
                <div className="typing-indicator h-[20px]">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              ) : (
                renderContent()
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenderAiPlanner;
